// The geometry of a sheet of equipment stickers, in millimetres.
//
// The stickers sit edge to edge in a grid, so every cut is one straight line
// across the sheet. The numbers are those of the sheets printed and tested by
// hand before this page existed; change them together or not at all.

export const SHEET = {
  /** A4, portrait. */
  pageWidth: 210,
  pageHeight: 297,
  columns: 4,
  rows: 4,
  stickerWidth: 46,
  stickerHeight: 64,
  /** Inside each sticker. */
  padding: 2.5,
  logo: 12,
  code: 30,
  /** Modules a side of the code the API draws for a label. */
  codeModules: 25,
  /** Clear modules kept around the code, so that it can be found. */
  quietModules: 4,
  /** Marks in the margin, in line with every cut. */
  markGap: 2.5,
  markLength: 4,
} as const;

export const STICKERS_PER_PAGE = SHEET.columns * SHEET.rows;

/** Clear space kept on each side of the code, in millimetres. */
export const QUIET_ZONE = (SHEET.quietModules * SHEET.code) / SHEET.codeModules;

/** Where the grid starts on the page, so that it sits in the middle. */
export const GRID_LEFT = (SHEET.pageWidth - SHEET.columns * SHEET.stickerWidth) / 2;
export const GRID_TOP = (SHEET.pageHeight - SHEET.rows * SHEET.stickerHeight) / 2;

/** The most stickers of one equipment a sheet run may hold. */
export const MAX_COPIES = 99;

/**
 * How many stickers of an equipment to print, from what was typed: a whole
 * number from one to MAX_COPIES. Anything that is not a number is one.
 */
export function copiesFrom(typed: string | undefined): number {
  const asked = Math.floor(Number(typed));
  if (!Number.isFinite(asked) || asked < 1) return 1;
  return Math.min(MAX_COPIES, asked);
}

/**
 * The stickers to print, in order: each item as many times as it is asked
 * for, its copies side by side.
 */
export function repeat<T>(items: readonly T[], copies: (item: T) => number): T[] {
  return items.flatMap((item) => Array.from({ length: Math.max(0, copies(item)) }, () => item));
}

/** Splits the stickers into sheets, in order. */
export function paginate<T>(items: readonly T[]): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += STICKERS_PER_PAGE) {
    pages.push(items.slice(i, i + STICKERS_PER_PAGE));
  }
  return pages;
}

export interface CutLines {
  /** Distances from the grid's left edge, in millimetres. */
  vertical: number[];
  /** Distances from the grid's top edge, in millimetres. */
  horizontal: number[];
  /** How far the vertical cuts run down, and the horizontal ones across. */
  height: number;
  width: number;
}

/**
 * The cuts a sheet holding `count` stickers needs: only around the stickers
 * that are there, so a sheet with seven is not ruled for sixteen.
 */
export function cutLines(count: number): CutLines {
  const used = Math.max(0, Math.min(count, STICKERS_PER_PAGE));
  const rows = Math.ceil(used / SHEET.columns);
  const columns = Math.min(used, SHEET.columns);
  return {
    vertical: Array.from({ length: columns + (used > 0 ? 1 : 0) }, (_, i) => i * SHEET.stickerWidth),
    horizontal: Array.from({ length: rows + (used > 0 ? 1 : 0) }, (_, i) => i * SHEET.stickerHeight),
    height: rows * SHEET.stickerHeight,
    width: columns * SHEET.stickerWidth,
  };
}

/**
 * Whether sticker `index` (0-based, within its sheet) has a neighbour to its
 * right or below. Where it has none its own edge is drawn; where it has one
 * the neighbour's edge does the job, so that no line is drawn twice.
 */
export function stickerEdges(index: number, count: number) {
  const column = index % SHEET.columns;
  return {
    drawRight: column === SHEET.columns - 1 || index + 1 >= count,
    drawBottom: index + SHEET.columns >= count,
  };
}

/** Line height of a sticker's name, as a multiple of its size. */
export const NAME_LINE_HEIGHT = 1.15;

/** The largest size a name is set in. It must fit on one line at this size. */
export const NAME_SIZE_ONE_LINE = 9.5;

/** The largest size of a name that runs to two lines. */
export const NAME_SIZE_TWO_LINES = 8.5;

/** Room under the code's clear space for the name, in millimetres. */
export const NAME_ROOM =
  SHEET.stickerHeight - 2 * SHEET.padding - SHEET.logo - SHEET.code - 2 * QUIET_ZONE;

/**
 * The size, in points, a name is set in so that it fits under the code in at
 * most two lines. A bold sans averages a little under 0.6 of its size per
 * character, which puts about twenty on a line at the largest size.
 */
export function nameSize(name: string): number {
  const length = name.trim().length;
  if (length <= 20) return NAME_SIZE_ONE_LINE;
  if (length <= 44) return NAME_SIZE_TWO_LINES;
  if (length <= 52) return 7.5;
  return 6.5;
}
